import { ADMIN_EMAILS } from "../config/admins";

const CURRENT_USER_KEY = "currentUserEmail";
const KNOWN_USERS_KEY = "knownUsers";

const normalize = (email) =>
  typeof email === "string" ? email.trim().toLowerCase() : "";

const readKnownUsers = () => {
  try {
    const raw = localStorage.getItem(KNOWN_USERS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeKnownUsers = (users) => {
  try {
    localStorage.setItem(KNOWN_USERS_KEY, JSON.stringify(users));
  } catch {
    // ignore quota errors; known-users is best-effort
  }
};

const rememberUser = (email) => {
  const normalized = normalize(email);
  if (!normalized) return;
  const users = readKnownUsers();
  if (!users.includes(normalized)) {
    users.push(normalized);
    writeKnownUsers(users.sort());
    window.dispatchEvent(
      new CustomEvent("knownUsersUpdated", { detail: { users } })
    );
  }
};

export const getKnownUsers = () => readKnownUsers();

export const setCurrentUser = (email) => {
  const normalized = normalize(email);
  if (!normalized) {
    localStorage.removeItem(CURRENT_USER_KEY);
    return;
  }
  localStorage.setItem(CURRENT_USER_KEY, normalized);
  rememberUser(normalized);
  window.dispatchEvent(
    new CustomEvent("currentUserChanged", { detail: { email: normalized } })
  );
};

export const clearCurrentUser = () => {
  localStorage.removeItem(CURRENT_USER_KEY);
  window.dispatchEvent(
    new CustomEvent("currentUserChanged", { detail: { email: null } })
  );
};

export const getCurrentUserEmail = () => {
  try {
    return localStorage.getItem(CURRENT_USER_KEY) || "";
  } catch {
    return "";
  }
};

const ADMIN_SET = new Set(ADMIN_EMAILS.map(normalize));

export const isAdminEmail = (email) => ADMIN_SET.has(normalize(email));

export const isCurrentUserAdmin = () => isAdminEmail(getCurrentUserEmail());

// localStorage keys holding user-scoped app data. Cleared on logout so a shared
// clinic workstation never hands one user's data to the next. Everything here
// is either a hydrated cache of a Supabase table (patients/trials/forms/
// requestedTrials/auditLog re-populate on the next login via hydrateStudyData /
// hydrateRequestedTrials / syncRemoteAuditLogToCache) or per-user state we
// deliberately drop (activity feed, notification read-state, cached account
// details).
//
// Deliberately NOT cleared:
//   - knownUsers: a DEVICE-wide roster of everyone who has signed in here, not
//     one user's data. Admin surfaces depend on it across logins (AuditLog's
//     user-performance table, userPerformance's default roster, the Leads
//     owner list), and it holds only email addresses — wiping it per-logout
//     would degrade those views on a multi-user workstation for little privacy
//     gain.
//   - device preferences (settings_display, settings_language,
//     settings_notifications, settings_privacy, settings_security).
//   - remember-me (rememberMe / rememberedEmail — persisting past logout is
//     their purpose) and qentoros-last-activity (harmless timestamp).
//   - qentoros-auth: Supabase clears its own session key on signOut.
const USER_SCOPED_STORAGE_KEYS = [
  "patients",
  "trials",
  "incidentalForms",
  "requestedTrials",
  "auditLog",
  "userActivities",
  "notifications",
  "settings_account",
];

/**
 * Wipe all user-scoped app data from localStorage. Called when a signed-in
 * session ends (logout button, inactivity expiry, recovery-flow sign-out) —
 * see AuthContext.applySession. Safe to call repeatedly.
 */
export const clearUserScopedStorage = () => {
  USER_SCOPED_STORAGE_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage unavailable (private mode, quota) — nothing to clear.
    }
  });
};
